import React, { useEffect, useState, useRef } from 'react';
import { Box, Stack, Tooltip, Typography, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, ArrowDropUp, ArrowDropDown, RemoveCircleOutline } from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { marketdata } from '../../services/platformApi';

/**
 * Top-nav live commodity ticker — Gas (TTF) + Carbon (EUA), used in CCGT
 * marginal-cost calculation. Public-adapter sources:
 *   - TTF  ← Yahoo NG=F (Henry Hub futures, USD/MMBtu — proxy for now)
 *   - EUA  ← Yahoo KRBN ETF (USD/share — proxy for EU carbon)
 *
 * Each instrument shows:
 *   value · Δ vs previous close (coloured) · tiny sparkline of recent ticks
 *
 * Hover: full attribution + source. Click eye icon to hide/show; choice
 * persists in localStorage. Volatility = the change vs previous close.
 */

const INSTRUMENTS = [
  { key: 'TTF', label: 'Gas',    color: '#ff6b6b' },
  { key: 'EUA', label: 'Carbon', color: '#a78bfa' },
];

const POLL_MS = 60_000;            // commodities are slow; once a minute is plenty
const SPARK_CAP = 30;              // ~30 minutes of history at 60s polling
const STORAGE_KEY = 'ecos_commodity_ticker_visible';

export default function CommodityTicker() {
  const [visible, setVisible] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw == null ? true : raw === 'true';
    } catch { return true; }
  });
  const [marks, setMarks] = useState({});
  const sparkRef = useRef({}); // { TTF: [{ts,v}], EUA: [{ts,v}] }

  const refresh = async () => {
    try {
      const results = await Promise.all(
        INSTRUMENTS.map((i) => marketdata.commodity({ instrument: i.key, adapter: 'public' }).catch((e) => ({ data: { instrument: i.key, error: e.message } })))
      );
      const next = {};
      const ts = Date.now();
      for (let i = 0; i < INSTRUMENTS.length; i++) {
        const k = INSTRUMENTS[i].key;
        const d = results[i]?.data || results[i];
        next[k] = d;
        if (d?.value != null) {
          const cur = sparkRef.current[k] || [];
          cur.push({ ts, v: d.value });
          if (cur.length > SPARK_CAP) cur.shift();
          sparkRef.current[k] = cur;
        }
      }
      setMarks(next);
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const toggle = () => {
    setVisible((v) => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  };

  if (!visible) {
    return (
      <Tooltip title="Show gas + carbon ticker" arrow>
        <IconButton size="small" onClick={toggle} sx={{ color: 'rgba(255,255,255,0.45)' }}>
          <VisibilityOff fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.6}
      sx={{
        px: 1, py: 0.5,
        borderRadius: 1.5,
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(255,255,255,0.03)',
      }}
    >
      {INSTRUMENTS.map((i, idx) => {
        const m = marks[i.key];
        const v = m?.value;
        const prev = m?.previous_close;
        const delta = (v != null && prev != null) ? v - prev : null;
        const pct = (delta != null && prev) ? (delta / prev) * 100 : null;
        const dColor = delta == null ? 'rgba(255,255,255,0.4)' : delta > 0 ? '#00ff88' : delta < 0 ? '#ff6b6b' : 'rgba(255,255,255,0.5)';
        const Arrow = delta == null ? RemoveCircleOutline : delta > 0 ? ArrowDropUp : delta < 0 ? ArrowDropDown : RemoveCircleOutline;
        const spark = sparkRef.current[i.key] || [];

        const tip = (
          <Box sx={{ fontSize: 11, fontFamily: 'monospace' }}>
            <Box sx={{ color: '#00d4ff', fontWeight: 700 }}>{i.label} ({i.key})</Box>
            <Box>last     {v != null ? v : '—'} {m?.unit || ''}</Box>
            <Box>prev close {prev != null ? prev : '—'}</Box>
            {pct != null && <Box sx={{ color: dColor }}>Δ {delta > 0 ? '+' : ''}{delta.toFixed(2)} ({pct > 0 ? '+' : ''}{pct.toFixed(2)}%)</Box>}
            {m?.symbol && <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>symbol {m.symbol}</Box>}
            {m?.source && <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>source {m.source}</Box>}
            {m?.note && <Box sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>{m.note}</Box>}
            {m?.ts && <Box sx={{ color: 'rgba(255,255,255,0.4)' }}>updated {new Date(m.ts).toLocaleTimeString()}</Box>}
          </Box>
        );

        return (
          <React.Fragment key={i.key}>
            {idx > 0 && <Box sx={{ width: 1, height: 18, bgcolor: 'rgba(255,255,255,0.1)' }} />}
            <Tooltip title={tip} arrow>
              <Stack direction="row" alignItems="center" spacing={0.4} sx={{ cursor: 'help', px: 0.4 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, fontFamily: 'monospace' }}>
                  {i.label}
                </Typography>
                <Typography variant="caption" sx={{ color: i.color, fontWeight: 700, fontFamily: 'monospace', fontSize: 11 }}>
                  {v != null ? v : '—'}
                </Typography>
                {/* sparkline */}
                {spark.length >= 2 && (
                  <Box sx={{ width: 36, height: 14 }}>
                    <ResponsiveContainer>
                      <LineChart data={spark} margin={{ top: 1, right: 0, left: 0, bottom: 1 }}>
                        <Line type="monotone" dataKey="v" stroke={dColor} strokeWidth={1.2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
                {/* delta */}
                <Stack direction="row" alignItems="center" sx={{ color: dColor }}>
                  <Arrow sx={{ fontSize: 13, ml: -0.4 }} />
                  <Typography variant="caption" sx={{ color: dColor, fontSize: 10, fontFamily: 'monospace' }}>
                    {pct != null ? `${Math.abs(pct).toFixed(1)}%` : '—'}
                  </Typography>
                </Stack>
              </Stack>
            </Tooltip>
          </React.Fragment>
        );
      })}
      <Tooltip title="Hide ticker" arrow>
        <IconButton size="small" onClick={toggle} sx={{ color: 'rgba(255,255,255,0.45)', ml: 0.3 }}>
          <Visibility fontSize="small" sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
